import type {
  ApDocumentType,
  ApJobMatchStrategy,
  ApPurchaseDocument,
  ApReceipt,
} from "@/accounts-payable/types";

export const AP_LIST_PATH = "/accounts-payable";
const INVOICE_REVIEW_PATH = `${AP_LIST_PATH}/invoices`;
const RECEIPT_REVIEW_PATH = `${AP_LIST_PATH}/receipts`;

export const AP_TAB = {
  review: "review",
  automated: "automated",
  invoices: "invoices",
  receipts: "receipts",
  emails: "emails",
  vendors: "vendors",
  quickbooks: "quickbooks",
} as const;

export type ApTabValue = (typeof AP_TAB)[keyof typeof AP_TAB];

export const AP_TAB_LABEL: Record<ApTabValue, string> = {
  review: "Review",
  automated: "Automated",
  invoices: "Invoices",
  receipts: "Receipts",
  emails: "Emails",
  vendors: "Vendors",
  quickbooks: "QuickBooks",
};

export const AP_JOB_MATCH_STRATEGY_LABEL: Record<ApJobMatchStrategy, string> = {
  purchase_order: "matched by PO",
  job_number: "matched by job number",
  customer_name: "matched by customer",
  agent: "matched by agent",
  manual: "matched manually",
  non_job: "non-job expense",
};

export const jobMatchStrategyLabel = (
  strategy: ApJobMatchStrategy | null,
): string | null =>
  strategy ? (AP_JOB_MATCH_STRATEGY_LABEL[strategy] ?? strategy) : null;

export const parseApTab = (value: string | null): ApTabValue | null =>
  Object.values(AP_TAB).find((tab) => tab === value) ?? null;

/** Kept across AP tabs and into the review pages: the Review view's filters. */
export const AP_CARRIED_PARAMS = [
  "business",
  "action",
  "assignee",
  "vendor",
  "q",
] as const;

const withApParams = (
  path: string,
  tab?: ApTabValue | null,
  carry?: string,
) => {
  const from = new URLSearchParams(carry);
  const params = new URLSearchParams(tab ? { tab } : undefined);
  for (const key of AP_CARRIED_PARAMS) {
    const value = from.get(key);
    if (value) params.set(key, value);
  }
  const query = params.toString();
  return query ? `${path}?${query}` : path;
};

export const apListPath = (tab?: ApTabValue | null, carry?: string) =>
  withApParams(tab ? `${AP_LIST_PATH}/${tab}` : AP_LIST_PATH, undefined, carry);

const isApPath = (pathname: string) => {
  const after = pathname.slice(AP_LIST_PATH.length);
  return (
    pathname.startsWith(AP_LIST_PATH) && (after === "" || after.startsWith("/"))
  );
};

const isDocumentReviewPath = (pathname: string) =>
  pathname.startsWith(`${INVOICE_REVIEW_PATH}/`) ||
  pathname.startsWith(`${RECEIPT_REVIEW_PATH}/`);

/** A document keeps the tab it was opened from. */
export const apActiveTab = (
  pathname: string,
  search = "",
): ApTabValue | null => {
  if (!isApPath(pathname)) return null;
  const listed = Object.values(AP_TAB).find(
    (tab) => pathname === `${AP_LIST_PATH}/${tab}`,
  );
  if (listed) return listed;
  if (pathname === AP_LIST_PATH) return AP_TAB.review;
  if (!isDocumentReviewPath(pathname)) return null;
  return parseApTab(new URLSearchParams(search).get("tab")) ?? AP_TAB.review;
};

export const invoiceReviewPath = (
  invoiceId: string,
  fromTab?: ApTabValue | null,
  carry?: string,
) => withApParams(`${INVOICE_REVIEW_PATH}/${invoiceId}`, fromTab, carry);

export const receiptReviewPath = (
  receiptId: string,
  fromTab?: ApTabValue | null,
  carry?: string,
) => withApParams(`${RECEIPT_REVIEW_PATH}/${receiptId}`, fromTab, carry);

export const documentLabel = (
  document: Pick<
    ApPurchaseDocument,
    "vendor_name" | "document_type" | "document_number"
  >,
): string =>
  [
    document.vendor_name,
    document.document_type ?? "invoice",
    document.document_number,
  ]
    .filter(Boolean)
    .join(" ");

export const documentTypeLabel = (documentType: ApDocumentType) =>
  ({
    invoice: { lower: "invoice", capitalized: "Invoice" },
    receipt: { lower: "receipt", capitalized: "Receipt" },
  })[documentType];

export const receiptPaymentLabel = (
  receipt: Pick<ApReceipt, "payment_method" | "card_last4">,
): string | null =>
  [receipt.payment_method, receipt.card_last4 && `•••• ${receipt.card_last4}`]
    .filter(Boolean)
    .join(" ") || null;
